import { emptyFormErrors, hasFormErrors, type FormErrors } from '@/lib/form-errors';

/** Wording matches the backend's sign-off checks so both paths read the same. */
export const RESCUE_PLAN_SIGN_OFF_MESSAGES = {
  steps: 'At least one rescue step is required before sign-off.',
  rescue_team_description: 'At least one rescue team member is required before sign-off.',
  communication_protocol: 'A communication protocol is required before sign-off.',
  emergency_services_phone: 'An emergency services number is required before sign-off.',
} as const;

export const BLANK_STEP_MESSAGE = 'Enter a description for this step.';

interface StepLike {
  description: string;
}

function blankStepRows(steps: StepLike[]): Record<number, Record<string, string[]>> {
  const rows: Record<number, Record<string, string[]>> = {};
  steps.forEach((step, index) => {
    if (!step.description.trim()) rows[index] = { description: [BLANK_STEP_MESSAGE] };
  });
  return rows;
}

export interface RescuePlanFormValues {
  steps: StepLike[];
  rescueTeamDescription: string;
  communicationProtocol: string;
  emergencyServicesPhone: string;
}

/**
 * Problems the backend would reject, caught before the request is sent.
 * Blank steps fail on any save; the sign-off content only matters for
 * "Create & approve".
 */
export function validateRescuePlanForm(
  values: RescuePlanFormValues,
  { approve }: { approve: boolean },
): FormErrors | null {
  const errors = emptyFormErrors();
  const rows = blankStepRows(values.steps);
  if (Object.keys(rows).length) errors.rows.steps = rows;

  if (approve) {
    if (values.steps.length === 0) {
      errors.fields.steps = [RESCUE_PLAN_SIGN_OFF_MESSAGES.steps];
    }
    if (!values.rescueTeamDescription.trim()) {
      errors.fields.rescue_team_description = [RESCUE_PLAN_SIGN_OFF_MESSAGES.rescue_team_description];
    }
    if (!values.communicationProtocol.trim()) {
      errors.fields.communication_protocol = [RESCUE_PLAN_SIGN_OFF_MESSAGES.communication_protocol];
    }
    if (!values.emergencyServicesPhone.trim()) {
      errors.fields.emergency_services_phone = [RESCUE_PLAN_SIGN_OFF_MESSAGES.emergency_services_phone];
    }
  }
  return hasFormErrors(errors) ? errors : null;
}

export function validateWorkPlanForm(values: { steps: StepLike[] }): FormErrors | null {
  const errors = emptyFormErrors();
  const rows = blankStepRows(values.steps);
  if (Object.keys(rows).length) errors.rows.steps = rows;
  return hasFormErrors(errors) ? errors : null;
}

/** True when the backend refused because the job already has a current plan. */
export function isExistingPlanConflict(messages: string[] | undefined): boolean {
  return Boolean(messages?.some((message) => /already has a/i.test(message)));
}
